// components/OpenHours/ScheduledOrdersToggle.js
// ----------------------------------------------------------------------------
// "Accept scheduled orders", for every vendor type.
//
// Self-contained on purpose so every hours screen gets the same switch by
// passing `vendorType` and `vendorId` to OpenHoursManager:
//   read:  GET  <base>/:id/open-status   -> acceptsScheduledOrders
//   write: PATCH <base>/:id/open-hours   { acceptsScheduledOrders: boolean }
// The write goes through the same guarded route as the hours themselves, so the
// vendor, and platform staff managing that vendor, can change it and nobody
// else can. It saves on toggle (optimistic, reverted on failure) rather than
// waiting for "Save Business Hours", so it never rides along with a half-edited
// schedule.
// ----------------------------------------------------------------------------

import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, FormControlLabel, Switch, Typography } from '@material-ui/core';
import { toast } from 'react-toastify';
import { apiGetVendorOpenStatus, apiUpdateVendorOpenHours } from '../../api';

export const SCHEDULED_ORDERS_HELP =
  'Enabled by default. Customers can order ahead for a time when you are open. Switch this off to accept express orders only.';

const ScheduledOrdersToggle = ({ vendorType, vendorId, disabled = false }) => {
  const [enabled, setEnabled] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  // Guards against a slow read landing after the vendor already toggled.
  const touchedRef = useRef(false);

  useEffect(() => {
    let alive = true;
    touchedRef.current = false;
    setLoaded(false);
    setLoadError(false);
    if (!vendorType || !vendorId) return undefined;
    apiGetVendorOpenStatus(vendorType, vendorId)
      .then((res) => {
        if (alive && !touchedRef.current) {
          setEnabled(res?.data?.acceptsScheduledOrders !== false);
          setLoaded(true);
        }
      })
      .catch(() => {
        if (alive) setLoadError(true);
      });
    return () => {
      alive = false;
    };
  }, [vendorType, vendorId, loadAttempt]);

  const handleChange = async (event) => {
    const next = event.target.checked;
    touchedRef.current = true;
    setEnabled(next);
    setSaving(true);
    try {
      await apiUpdateVendorOpenHours(vendorType, vendorId, { acceptsScheduledOrders: next });
      toast.success(next ? 'Scheduled orders turned on' : 'Scheduled orders turned off');
    } catch (err) {
      setEnabled(!next);
      toast.error(err?.response?.data?.message || 'Could not update scheduled orders');
    } finally {
      setSaving(false);
    }
  };

  if (!vendorType || !vendorId) return null;

  return (
    <Box
      mb={2}
      p={1.5}
      style={{ backgroundColor: '#f5f7fb', border: '1px solid #dfe6f0', borderRadius: 8 }}
    >
      <FormControlLabel
        control={
          <Switch
            color="primary"
            checked={enabled}
            onChange={handleChange}
            disabled={disabled || saving || !loaded}
            inputProps={{ 'aria-label': 'Accept scheduled orders' }}
          />
        }
        label={
          <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>
            Accept scheduled orders
          </span>
        }
      />
      <Typography style={{ fontSize: '0.8rem', color: '#555', marginLeft: 4 }}>
        {SCHEDULED_ORDERS_HELP}
      </Typography>
      {loadError && (
        <Typography style={{ fontSize: '0.8rem', color: '#555', marginLeft: 4 }}>
          Could not load your scheduled order setting.
          <Button size="small" onClick={() => setLoadAttempt((value) => value + 1)}>Try again</Button>
        </Typography>
      )}
    </Box>
  );
};

export default ScheduledOrdersToggle;
